import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';
import clickOutside from '@adsk/bim360-matrix-react-helpers/es/clickOutsideHOC';
import getPropsForComponent from '@adsk/bim360-matrix-react-helpers/es/getPropsForComponent';
import ellipsis from '@adsk/bim360-matrix-react-helpers/es/ellipsis';
import measureText from '@adsk/bim360-matrix-react-helpers/es/measureText';
import {
  mobileWidthThreshold,
  accountProjectPickerWidth
} from '@adsk/bim360-matrix-react-helpers/es/consts';

import Tabs from '@adsk/bim360-matrix-react-tabs';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import { H1 } from '@adsk/bim360-matrix-react-h';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import ModulePicker from '@adsk/bim360-matrix-react-module-picker';
import UserMenu from '@adsk/bim360-matrix-react-user-menu';
import MatrixHelpMenu from '@adsk/bim360-matrix-react-matrix-help-menu';
import UserAvatar from '@adsk/bim360-matrix-react-user-avatar';
import AccountProjectPicker from '@adsk/bim360-matrix-react-account-project-picker';
import SwitcherMenu from '@adsk/bim360-matrix-react-switcher-menu';
import { ConnectedBanner as Banner } from '@adsk/bim360-matrix-react-banner';
import InlineLoader from '@adsk/bim360-matrix-react-inline-loader';
import NotificationsSettingsModal from '@adsk/bim360-matrix-react-notifications-settings-modal';

import menuArrow from '@adsk/bim360-matrix-react-assets/menu-arrow.svg';
import LogoSvg from '@adsk/bim360-matrix-react-assets/logo.svg';
import bim360Mark from '@adsk/bim360-matrix-react-assets/bim360-mark.svg';
import accMark from '@adsk/bim360-matrix-react-assets/acc-mark.svg';
import mobileLogo from '@adsk/bim360-matrix-react-assets/mobile-logo.svg';
import avatarIcon from '@adsk/bim360-matrix-react-assets/icon-avatar-menu.svg';
import modulePickerSvg from '@adsk/bim360-matrix-react-assets/module-picker.svg';
import titleTriangleSvg from '@adsk/bim360-matrix-react-assets/matrix-header-title-triangle.svg';

import './_MatrixHeader.scss';
import { OPEN_EMAIL_SETTINGS_QUERY_PARAM } from './helpers/consts';
import IENotSupportedModal from './IENotSupportedModal';
import AccOrFedRAMPLogo from './AccOrFedRAMPLogo';
import { ProjectConsumer } from '@adsk/bim360-matrix-react-api';
import IsFedrampRegion from './IsFedrampRegion';
import PageIdleTimer from './PageIdleTimer';

const ClickOutsideModulePicker = clickOutside(ModulePicker);
const ClickOutsideUserMenu = clickOutside(UserMenu);
const ClickOutsideAccountProjectPicker = clickOutside(AccountProjectPicker);
const ClickOutsideTabsDropdown = clickOutside(SwitcherMenu);

const PICKER_PADDING = 15;

const invokeOnce = cb => {
  let wasCalled = false;

  return (...args) => {
    if (!wasCalled && args.every(arg => arg !== null && arg !== undefined)) {
      cb(...args);
      wasCalled = true;
    }
  };
};

const clearQueryParamIfExists = param => {
  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.has(param)) {
    queryParams.delete(param);
    window.history.replaceState(undefined, undefined, `?${queryParams.toString()}`);
  }
};

const ArrowDown = ({ className = '', ...props }) => {
  return (
    <SvgIcon width="16" height="10" {...props} className={`MatrixHeader__arrow-down ${className}`}>
      {menuArrow}
    </SvgIcon>
  );
};

ArrowDown.propTypes = {
  className: PropTypes.string
};

/**
 * The MatrixHeader component that displays all the common Matrix header visuals.
 * MatrixHeader is a smart component - requiring `MatrixProvider` somewhere up in react components tree.
 * If `open-email-settings` URL param exists on mount, it would trigger opening `NotificationsSettingsModal` and remove it once closed.
 **/
export default class MatrixHeader extends Component {
  /**
   * @constructor MatrixHeader
   **/
  constructor(props) {
    super(props);

    this.state = {
      showModulePicker: false,
      showUserMenu: false,
      showTabsDropdown: false,
      accountProjectPickerLeft: 0,
      showAccountProjectPicker: false,
      isMobileView: false,
      notificationSettingsOpen: new URLSearchParams(window.location.search).has(
        OPEN_EMAIL_SETTINGS_QUERY_PARAM
      )
    };

    this._handleWindowResize = debounce(this._handleWindowResize.bind(this), 100);
    this._handleAccountProjectTriggerClick = this._handleAccountProjectTriggerClick.bind(this);
    this._setEvergage = invokeOnce(this._setEvergage);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._handleWindowResize);
    this._handleWindowResize.cancel();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this._setEvergage(nextProps.evergageIntegrationUrl, nextProps.selectedProject, nextProps.user);
  }

  componentDidMount() {
    this._setEvergage(
      this.props.evergageIntegrationUrl,
      this.props.selectedProject,
      this.props.user
    );

    window.addEventListener('resize', this._handleWindowResize);
    this._handleWindowResize();
  }

  componentDidUpdate(prevProps) {
    if (
      !this._hasSelectedAccountOrProject(prevProps) &&
      this._hasSelectedAccountOrProject(this.props)
    ) {
      this.forceUpdate();
    }
  }

  _setEvergage(evergageIntegrationUrl, selectedProject, user) {
    const isUserProjectAdmin = selectedProject.services.find(
      service => service.type === 'project_admin'
    );
    window.userEmail = user.email;
    window.adminOnActiveProject = isUserProjectAdmin;
    window.userIsAdmin = isUserProjectAdmin;

    require('./helpers/evergageFlickerDefender.min');
    require('./helpers/evergageAsync').default(evergageIntegrationUrl);
  }

  onExitNotificationsSettings = () => {
    this.setState({ notificationSettingsOpen: false });
    clearQueryParamIfExists(OPEN_EMAIL_SETTINGS_QUERY_PARAM);
  };

  _handleWindowResize() {
    const isMobileView = window.innerWidth <= mobileWidthThreshold;
    const screenMiddle = window.innerWidth / 2;
    const triggerBoundingRect = this._getAccountProjectPickerTriggerBoundingRect();
    if (triggerBoundingRect) {
      const pickerLeft = triggerBoundingRect.left;
      const pickerMiddle = accountProjectPickerWidth / 2 + pickerLeft;
      const accountProjectPickerLeft = isMobileView
        ? screenMiddle - pickerMiddle
        : this.state.accountProjectPickerLeft;
      const adjustedAccountProjectPickerLeft = this._adjustAccountProjectPickerLeftWithinScreen(
        accountProjectPickerLeft,
        triggerBoundingRect
      );

      this.setState({ accountProjectPickerLeft: adjustedAccountProjectPickerLeft, isMobileView });
    }
  }

  _hasSelectedAccountOrProject(props = this.props) {
    return props.selectedAccount || props.selectedProject;
  }

  /**
   *
   * @returns {VDOM}
   */
  renderModulePickerOverlay() {
    const toggleName = 'showModulePicker';
    const modulePickerProps = {
      ...getPropsForComponent(this.props, ModulePicker),
      onActionClicked: () => this.toggleState(toggleName),
      handleClickOutside: () => this.toggleState(toggleName),
      rootClose: true,
      target: this.modulePickerTargetRef,
      eventTypes: 'click'
    };

    return <ClickOutsideModulePicker {...modulePickerProps} />;
  }

  /**
   *
   * @param stateProperty
   */
  toggleState(stateProperty, extraState) {
    this.setState({
      [stateProperty]: !this.state[stateProperty],
      ...extraState
    });
  }

  /**
   *
   * @returns {VDOM}
   */
  renderUserMenu() {
    const userMenuProps = {
      ...getPropsForComponent(this.props, UserMenu),
      rootClose: true,
      target: this.userContainerRef,
      handleClickOutside: () => this.toggleState('showUserMenu'),
      openNotificationSettings: () =>
        this.toggleState('notificationSettingsOpen', { showUserMenu: false }),
      eventTypes: 'click'
    };

    return (
      <ProjectConsumer>
        {project => (
          <IsFedrampRegion isFedRAMP={this.props.isFedRAMP}>
            {({ isFedRAMP }) => (
              <ClickOutsideUserMenu
                {...userMenuProps}
                isFedRAMP={isFedRAMP}
                hideNotificationSettings={!(project && project.id)}
              />
            )}
          </IsFedrampRegion>
        )}
      </ProjectConsumer>
    );
  }

  renderNotificationSettingsModal() {
    return (
      <ProjectConsumer>
        {project => {
          if (project && project.id) {
            return (
              <NotificationsSettingsModal
                show={this.state.notificationSettingsOpen}
                onExited={this.onExitNotificationsSettings}
              />
            );
          }
          return null;
        }}
      </ProjectConsumer>
    );
  }

  renderTabsMobileDropdown() {
    const tabs = this.props.tabs.map(t => {
      const name = t.name();

      return { ...t, name };
    });
    const selectedTabName = this.props.selectedTab.name();
    const selectedItem = { ...this.props.selectedTab, name: selectedTabName };
    const toggleName = 'showTabsDropdown';
    const tabsDropdownProps = {
      menuItems: tabs,
      selectedItem,
      onSelect: this.props.onTabChange,
      rootClose: true,
      handleClickOutside: () => this.toggleState(toggleName),
      eventTypes: 'click'
    };

    return (
      <ClickOutsideTabsDropdown
        {...tabsDropdownProps}
        className="MatrixHeader__tabs-container-mobile-menu"
        onSelect={this.props.onTabChange}
      />
    );
  }

  renderTabs() {
    return (
      <div className="MatrixHeader__tabs-container">
        <Tabs
          className="MatrixHeader__tabs-container-main"
          tabs={this.props.tabs}
          onTabChange={this.props.onTabChange}
          selectedTab={this.props.selectedTab}
        />
        <div
          className="MatrixHeader__tabs-container-mobile"
          onClick={() => this.toggleState('showTabsDropdown')}
        >
          <span className="MatrixHeader__tabs-container-mobile-selected">
            {this.props.selectedTab.name()}
          </span>
          <ArrowDown className="MatrixHeader__tabs-container-mobile-selected-icon" />
          {this.state.showTabsDropdown && this.renderTabsMobileDropdown()}
        </div>
      </div>
    );
  }

  _getTextsForAccountProjectPicker(longerValue, shorterValue, { availableWidth, fontSize }) {
    // Default - trimming (if needed) the longer text value and keeping the shorter text value as is.
    const texts = {
      longer: ellipsis(longerValue.text, 0, availableWidth - shorterValue.width, fontSize),
      shorter: shorterValue.text
    };

    // If the longer text width after trimming is smaller than the shorter text width, it means we've trimmed too much.
    // In that case we split the available size to two parts and put as much as we can from each part.
    if (measureText(texts.longer) <= shorterValue.width) {
      texts.longer = ellipsis(longerValue.text, 0, availableWidth / 2, fontSize);
      texts.shorter = ellipsis(shorterValue.text, 0, availableWidth / 2, fontSize);
    }

    return texts;
  }

  _getProjectAndAccountName(theme = Themes.MATRIX) {
    const isAlloy = theme === Themes.ALLOY;

    let result = {};
    const originalProjectName = this.props.selectedProject ? this.props.selectedProject.name : '';
    const originalAccountName = this.props.selectedProject
      ? this.props.selectedProject.accountName
      : this.props.selectedAccount
        ? this.props.selectedAccount.name
        : '';
    const projectObject = { text: originalProjectName, width: measureText(originalProjectName) };
    const accountObject = { text: originalAccountName, width: measureText(originalAccountName) };

    if (this.accountProjectPickerRef && this.accountProjectPickerArrowRef) {
      if (isAlloy) {
        const totalPaddingOnWholeDiv = 16;
        // 8px is margin left
        const modulePickerTotalWidth =
          this.topPanelRef.offsetWidth +
          parseInt(window.getComputedStyle(this.topPanelRef).marginRight);
        const userBoxTotalWidth = this.topRightPanelRef.offsetWidth;
        // (16 + 8) is th B/ACC logo + 8 margin left
        // (32 + 12) is the thumbnail + 12 margin right
        const accountProjectPickerElementsTotalWidth =
          this.accountProjectPickerArrowRef.offsetWidth + (32 + 12) + (16 + 8);

        const alloyThemeAvailableWidth =
          window.innerWidth -
          totalPaddingOnWholeDiv -
          modulePickerTotalWidth -
          userBoxTotalWidth -
          accountProjectPickerElementsTotalWidth;

        const uiOptions = {
          availableWidth: alloyThemeAvailableWidth > 0 ? alloyThemeAvailableWidth : 340,
          fontSize: parseInt(window.getComputedStyle(this.accountProjectPickerRef).fontSize)
        };

        // in alloy theme we either display the account or the project, therefore trimming is only
        // needed for one of them, if project exists, we display it an trim it and apply the same on account when project
        // is missing.
        const texts = this._getTextsForAccountProjectPicker(
          this.props.selectedProject ? projectObject : accountObject,
          { text: '', width: 0 },
          uiOptions
        );

        result = {
          projectName: texts.longer,
          accountName: texts.longer
        };

        if (this.props.selectedProject) {
          result.imageUrl = this.props.selectedProject.imageUrl;
        }
      } else {
        // In the case we're only in account level there's no separator - which means we don't need to account for its width.
        const separatorWidth = this.accountProjectPickerSeparatorRef
          ? this.accountProjectPickerSeparatorRef.offsetWidth
          : 0;

        const availableWidth =
          window.innerWidth -
          parseInt(window.getComputedStyle(this.topPanelRef).marginLeft) * 2 -
          this.logoRef.offsetWidth -
          this.topRightPanelRef.offsetWidth -
          this.accountProjectPickerArrowRef.offsetWidth -
          separatorWidth;

        const uiOptions = {
          availableWidth: availableWidth > 0 ? availableWidth : 340,
          fontSize: parseInt(window.getComputedStyle(this.accountProjectPickerRef).fontSize)
        };

        if (isAlloy) {
          // in alloy theme we either display the account or the project, therefore trimming is only
          // needed for one of them, if project exists, we display it an trim it and apply the same on account when project
          // is missing.
          const texts = this._getTextsForAccountProjectPicker(
            this.props.selectedProject ? projectObject : accountObject,
            { text: '', width: 0 },
            uiOptions
          );

          result = { projectName: texts.longer, accountName: texts.shorter };
        } else if (accountObject.width < projectObject.width) {
          const texts = this._getTextsForAccountProjectPicker(
            projectObject,
            accountObject,
            uiOptions
          );
          result = { projectName: texts.longer, accountName: texts.shorter };
        } else {
          const texts = this._getTextsForAccountProjectPicker(
            accountObject,
            projectObject,
            uiOptions
          );
          result = { projectName: texts.shorter, accountName: texts.longer };
        }
      }
    } else {
      result = { projectName: '', accountName: '' };
    }

    return result;
  }

  _getAccountProjectPickerTriggerBoundingRect() {
    const triggerElement = this.accountProjectPickerRef.getElementsByClassName(
      'MatrixHeader__account-project-picker-trigger-title'
    )[0];
    return triggerElement ? triggerElement.getBoundingClientRect() : null;
  }

  _adjustAccountProjectPickerLeftWithinScreen(pickerLeft, triggerBoundingRect) {
    const minLeft = PICKER_PADDING - triggerBoundingRect.left;
    const pickerRight = triggerBoundingRect.left + accountProjectPickerWidth;
    const maxLeft = window.innerWidth - PICKER_PADDING - pickerRight;
    const adjustedLeft = Math.max(Math.min(pickerLeft, maxLeft), minLeft);

    return adjustedLeft;
  }

  _handleAccountProjectTriggerClick(event) {
    const desiredPosition = event.clientX;
    const triggerBoundingRect = this._getAccountProjectPickerTriggerBoundingRect();
    if (triggerBoundingRect) {
      const pickerMiddle = accountProjectPickerWidth / 2;
      const denormalizedAccountProjectPickerLeft = this.state.isMobileView
        ? this.state.accountProjectPickerLeft
        : desiredPosition - pickerMiddle - triggerBoundingRect.left;
      const accountProjectPickerLeft = this._adjustAccountProjectPickerLeftWithinScreen(
        denormalizedAccountProjectPickerLeft,
        triggerBoundingRect
      );
      this.toggleState('showAccountProjectPicker', { accountProjectPickerLeft });
    }
  }

  _renderAccountProjectPickerButton(theme) {
    const isAlloy = theme === Themes.ALLOY;
    const arrowClasses = classNames('MatrixHeader__selected-item-arrow', {
      'MatrixHeader__selected-item-arrow--open': this.state.showAccountProjectPicker
    });
    const accountProjectTitleClass = 'MatrixHeader__account-project-picker-trigger-title';
    const triggerTitleClasses = classNames(accountProjectTitleClass, {
      'MatrixHeader__account-project-picker-trigger-title--open': this.state
        .showAccountProjectPicker
    });

    const { accountName, projectName, imageUrl } = this._getProjectAndAccountName(theme);
    const accountImageUrl = this.props.selectedAccount && this.props.selectedAccount.imageUrl;

    const showAccountName = !isAlloy || !this.props.selectedProject;

    return (
      <AnchorButton
        className={triggerTitleClasses}
        data-testid={accountProjectTitleClass}
        onClick={this._handleAccountProjectTriggerClick}
      >
        {isAlloy && (
          <img
            src={imageUrl || accountImageUrl}
            width="32px"
            height="32px"
            className="MatrixHeader__account-project-picker-trigger-thumb"
          />
        )}
        {showAccountName && (
          <span className="MatrixHeader__account-project-picker-trigger-account">
            {accountName}
          </span>
        )}
        {!isAlloy && this.props.selectedProject && (
          <span
            ref={_ => (this.accountProjectPickerSeparatorRef = _)}
            className="MatrixHeader__account-project-picker-separator"
          >
            <SvgIcon width="5" height="8">
              {titleTriangleSvg}
            </SvgIcon>
          </span>
        )}
        {this.props.selectedProject && (
          <span className="MatrixHeader__account-project-picker-trigger-project">
            {projectName}
          </span>
        )}
        {this.props.selectedProject && isAlloy && (
          <span className="MatrixHeaderAlloy__product">
            <SvgIcon width="16px" height="16px">
              {this.getProductIcon()}
            </SvgIcon>
          </span>
        )}
        <span ref={_ => (this.accountProjectPickerArrowRef = _)}>
          <ArrowDown className={arrowClasses} transform="translate(0 0.5)" />
        </span>
      </AnchorButton>
    );
  }

  _renderPickerTrigger(theme = Themes.MATRIX) {
    return (
      <div
        className="MatrixHeader__account-project-picker-trigger"
        ref={_ => (this.accountProjectPickerRef = _)}
      >
        {!this._hasSelectedAccountOrProject() ? (
          <InlineLoader
            theme={InlineLoader.Theme.DARK}
            className="MatrixHeader__account-project-picker-loader"
          />
        ) : (
          this._renderAccountProjectPickerButton(theme)
        )}
        {this.state.showAccountProjectPicker && (
          <ClickOutsideAccountProjectPicker
            {...getPropsForComponent(this.props, AccountProjectPicker)}
            onProjectClick={project => {
              this.props.onSelectProject(project);
              this.toggleState('showAccountProjectPicker');
            }}
            onAccountClick={account => {
              this.props.onSelectAccount(account);
              this.toggleState('showAccountProjectPicker');
            }}
            className="MatrixHeader__account-project-picker"
            style={{ left: this.state.accountProjectPickerLeft }}
            rootClose={true}
            target={this.accountProjectPickerRef}
            handleClickOutside={() => this.toggleState('showAccountProjectPicker')}
            isLoadingProjects={this.props.entitlementsLoadingData.projects.isLoading}
            isLoadingAccounts={this.props.entitlementsLoadingData.accounts.isLoading}
            accountsLoadingError={this.props.entitlementsLoadingData.accounts.isBatchFailed}
            projectsLoadingError={this.props.entitlementsLoadingData.projects.isBatchFailed}
            onTryLoadingAgainClick={this.props.entitlementsLoadingData.onDataLoadAgain}
            eventTypes="click"
            theme={theme}
          />
        )}
      </div>
    );
  }

  getProductIcon = () => {
    const { selectedProject } = this.props;
    if (selectedProject) {
      return selectedProject.acsUnified ? accMark : bim360Mark;
    }

    return undefined;
  };

  renderModuleIcon = theme => {
    const { selectedModule } = this.props;
    const isAlloy = theme === Themes.ALLOY;
    const modulePickerIconClasses = classNames('MatrixHeader__module-picker-icon', {
      'MatrixHeader__module-picker-icon--open': this.state.showModulePicker
    });

    if (!selectedModule && isAlloy) return null;

    if (isAlloy) {
      return (
        <div className="MatrixHeader__module-picker-thumb">
          <img src={selectedModule.icon} width="24px" height="24px" />
        </div>
      );
    } else {
      return <SvgIcon className={modulePickerIconClasses}>{modulePickerSvg}</SvgIcon>;
    }
  };

  renderModulePicker(theme) {
    const isAlloy = theme === Themes.ALLOY;
    const modulePickerDisabled =
      !this.props.modules || this.props.modules.length === 0 || !this.props.selectedModule;
    const modulePickerContainerClasses = classNames('MatrixHeader__module-picker', {
      'MatrixHeader__module-picker--disabled': modulePickerDisabled
    });

    const additionalProps = {
      ref: _ => (this.topPanelRef = _)
    };

    return (
      <div className={modulePickerContainerClasses} {...(isAlloy && additionalProps)}>
        <div
          className="MatrixHeader__module-picker-trigger"
          onClick={() => !modulePickerDisabled && this.toggleState('showModulePicker')}
          ref={_ => (this.modulePickerTargetRef = _)}
        >
          {this.renderModuleIcon(theme)}
          {this.props.selectedModule ? (
            <H1 className="MatrixHeader__module-picker-title">
              {this.props.selectedModule.name}
              {isAlloy && <ArrowDown />}
            </H1>
          ) : (
            <InlineLoader
              className="MatrixHeader__module-picker-loader"
              theme={InlineLoader.Theme.DARK}
            />
          )}
        </div>
        {this.state.showModulePicker && this.renderModulePickerOverlay()}
      </div>
    );
  }

  renderAlloyTheme = () => {
    const { theme, isFedRAMP, ...rest } = this.props;

    return (
      <div>
        <header className="MatrixHeaderAlloy" role="banner">
          <div className="MatrixHeaderAlloy__surface">
            <div className="MatrixHeaderAlloy__logo" ref={_ => (this.logoRef = _)}>
              <AccOrFedRAMPLogo isFedRAMP={isFedRAMP} />
            </div>
            <div className="MatrixHeaderAlloy__content">
              {this.renderModulePicker(theme)}
              <div className="MatrixHeaderAlloy__project">
                <div className="MatrixHeaderAlloy__project--picker">
                  {this._renderPickerTrigger(theme)}
                </div>
              </div>
              <div className="MatrixHeaderAlloy__user-box" ref={_ => (this.topRightPanelRef = _)}>
                <MatrixHelpMenu
                  theme={theme}
                  {...getPropsForComponent(this.props, MatrixHelpMenu)}
                />
                <div
                  className="MatrixHeader__user-container"
                  ref={_ => (this.userContainerRef = _)}
                >
                  {this.props.user ? (
                    <div
                      className="MatrixHeaderAlloy__user-container"
                      onClick={() => this.toggleState('showUserMenu')}
                    >
                      <UserAvatar {...getPropsForComponent(rest, UserAvatar)} />
                      <span>{this.props.user.name}</span>
                      <ArrowDown />
                    </div>
                  ) : (
                    <div className="MatrixHeader__user-missing-icon">
                      <SvgIcon width="20px" height="20px">
                        {avatarIcon}
                      </SvgIcon>
                    </div>
                  )}
                  {this.state.showUserMenu && this.renderUserMenu()}
                </div>
              </div>
            </div>
          </div>
          {!isEmpty(this.props.tabs) && (
            <Tabs
              className="MatrixHeaderAlloy__tabs"
              tabs={this.props.tabs}
              onTabChange={this.props.onTabChange}
              selectedTab={this.props.selectedTab}
            />
          )}
        </header>
        <Banner />
        <IENotSupportedModal />
        <PageIdleTimer {...this.props} />
        {this.renderNotificationSettingsModal()}
      </div>
    );
  };

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const { theme, ...rest } = this.props;

    if (theme === Themes.ALLOY) return this.renderAlloyTheme();

    return (
      <div>
        <header className="MatrixHeader" role="banner">
          <div className="MatrixHeader__top-panel" ref={_ => (this.topPanelRef = _)}>
            <div className="MatrixHeader__title-bar">
              <div className="MatrixHeader__logo" ref={_ => (this.logoRef = _)}>
                <IconButton
                  className="MatrixHeader__logo--main"
                  onClick={e => this.props.onLogoClick(e)}
                >
                  <SvgIcon width="157" height="24">
                    {LogoSvg}
                  </SvgIcon>
                </IconButton>
                <IconButton
                  className="MatrixHeader__logo--mobile"
                  onClick={e => this.props.onLogoClick(e)}
                >
                  <SvgIcon width="30" height="40">
                    {mobileLogo}
                  </SvgIcon>
                </IconButton>
                <span className="MatrixHeader__separator" />
              </div>
              {this._renderPickerTrigger()}
            </div>
            <div className="MatrixHeader__top-right-panel" ref={_ => (this.topRightPanelRef = _)}>
              <MatrixHelpMenu {...getPropsForComponent(this.props, MatrixHelpMenu)} />
              <div className="MatrixHeader__user-container" ref={_ => (this.userContainerRef = _)}>
                {this.props.user ? (
                  <UserAvatar
                    {...getPropsForComponent(rest, UserAvatar)}
                    onClick={() => this.toggleState('showUserMenu')}
                  />
                ) : (
                  <div className="MatrixHeader__user-missing-icon">
                    <SvgIcon width="20px" height="20px">
                      {avatarIcon}
                    </SvgIcon>
                  </div>
                )}
                {this.state.showUserMenu && this.renderUserMenu()}
              </div>
            </div>
          </div>
          <div className="MatrixHeader__bottom-panel">
            {this.renderModulePicker()}
            {!isEmpty(this.props.tabs) ? (
              this.renderTabs()
            ) : (
              <div className="MatrixHeader__tabs-container" />
            )}
          </div>
        </header>
        <Banner />
        <IENotSupportedModal />
        <PageIdleTimer {...this.props} />
        {this.renderNotificationSettingsModal()}
      </div>
    );
  }
}

const entitlementsLoadingDataShape = {
  projects: PropTypes.shape({
    isLoading: PropTypes.bool,
    isBatchFailed: PropTypes.bool
  }),
  accounts: PropTypes.shape({
    isLoading: PropTypes.bool,
    isBatchFailed: PropTypes.bool
  }),
  onDataLoadAgain: PropTypes.func
};

MatrixHeader.Themes = Themes;

MatrixHeader.defaultProps = {
  onSelectProject: noop,
  onSelectAccount: noop,
  theme: Themes.MATRIX
};

MatrixHeader.propTypes = {
  ...ModulePicker.propTypes,
  ...Tabs.propTypes,
  ...UserMenu.propTypes,
  ...MatrixHelpMenu.propTypes,
  /**
   * Overriding the tabs type to make sure it's not required.
   */
  tabs: PropTypes.arrayOf(Tabs.tabShape),
  /**
   * The accounts list of the current user
   */
  accounts: PropTypes.arrayOf(PropTypes.shape(AccountProjectPicker.accountShape)),
  /**
   * The selected account
   */
  selectedAccount: PropTypes.shape(AccountProjectPicker.accountShape),
  /**
   * A function that's called when a different selection in the accounts menu is made
   */
  onSelectAccount: PropTypes.func,
  /**
   * The projects list of the current user
   */
  projects: PropTypes.arrayOf(PropTypes.shape(AccountProjectPicker.projectShape)),
  /**
   * The selected project
   */
  selectedProject: PropTypes.shape(AccountProjectPicker.projectShape),
  /**
   * The metadata for entitlements data
   */
  entitlementsLoadingData: PropTypes.shape(entitlementsLoadingDataShape),
  /**
   * A function that's called when a different selection in the projects menu is made
   */
  onSelectProject: PropTypes.func,
  /**
   * A function that's called when the logo is clicked
   */
  onLogoClick: PropTypes.func.isRequired,
  /**
   * The selected tab object to highlight in the tab navigation area
   */
  selectedTab: PropTypes.object,
  /**
   * URL for integrating Evergage
   */
  evergageIntegrationUrl: PropTypes.string,
  /**
   * The locale to use for this component, to override the inherited required locale and avoid warning
   */
  locale: PropTypes.string,
  /**
   * Flag for FedRAMP region modifications
   */
  isFedRAMP: PropTypes.bool,
  /**
   * Whether enable the page idle timer and auto trigger logout once exceeded the idle time
   */
  pageIdleEnabled: PropTypes.bool,
  /**
   * The maximum page idle time in milliseconds
   */
  pageIdleTimeInMs: PropTypes.number,
  /**
   * A function that's called when idle time exceeded. Fallback to UserMenu signout if not provided.
   */
  onIdleLogout: PropTypes.func,
  theme: PropTypes.oneOf(objectValues(Themes))
};
